import type { ExtrasOrderNode } from '@/views/FestivalShop'

export interface BillingRecipientEntry {
  order: ExtrasOrderNode
  sameKiosk: boolean
}

const BILLING_FIELDS = [
  'salutation',
  'title',
  'firstName',
  'lastName',
  'email',
  'company',
  'vatNumber',
  'customerReference',
  'street',
  'number',
  'additionalAddress',
  'zip',
  'city',
  'country',
] as const

function billingKey(order: ExtrasOrderNode): string {
  return BILLING_FIELDS.map((field) => ((order[field] as string | null | undefined) ?? '').trim().toLowerCase()).join('|')
}

function orderTimestamp(order: ExtrasOrderNode): number {
  const value = order.updatedAt || order.createdAt
  return value ? new Date(value as string).getTime() : 0
}

export function buildBillingRecipients(orders: ExtrasOrderNode[], currentKioskId: string): BillingRecipientEntry[] {
  const sorted = [...orders].sort((a, b) => orderTimestamp(b) - orderTimestamp(a))
  const sameKioskOrders = sorted.filter((order) => order.kiosk?.id === currentKioskId)
  const sameKioskKeys = new Set(sameKioskOrders.map(billingKey))

  const crossKioskByKey = new Map<string, ExtrasOrderNode>()
  sorted
    .filter((order) => order.kiosk?.id !== currentKioskId)
    .forEach((order) => {
      const key = billingKey(order)
      if (sameKioskKeys.has(key) || crossKioskByKey.has(key)) return
      crossKioskByKey.set(key, order)
    })

  return [
    ...sameKioskOrders.map((order) => ({ order, sameKiosk: true })),
    ...[...crossKioskByKey.values()].map((order) => ({ order, sameKiosk: false })),
  ].sort((a, b) => orderTimestamp(b.order) - orderTimestamp(a.order))
}

export function defaultRecipientId(entries: BillingRecipientEntry[]): string | null {
  const sameKiosk = entries.find((entry) => entry.sameKiosk)
  return (sameKiosk ?? entries.at(0))?.order.id ?? null
}
